import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { DangerAlert, InfoAlert, Loading, SuccessAlert } from '@techchange/components';
import { messages } from '../../../i18n';

const propTypes = {

	/**
	 * The state related to the projects update API call to Lokalise.
	 */
	apiStatusProject: PropTypes.shape({

		/**
		 * The Lokalise translation project.
		 */
		project: PropTypes.shape({

			/**
			 * The unique ID (from Lokalise) for the project.
			 */
			project_id: PropTypes.string.isRequired,

		}),

		/**
		 * True, if the API call is still in progress.
		 */
		saving: PropTypes.bool.isRequired,

		/**
		 * True, if the API call has succeeded.
		 */
		success: PropTypes.bool.isRequired,

		/**
		 * True, if the API call has failed.
		 */
		error: PropTypes.bool.isRequired,

	}).isRequired,

	/**
	 * The state related to the files upload API call to Lokalise.
	 */
	apiStatusUploadFiles: PropTypes.shape({

		/**
		 * True, if the API call is still in progress.
		 */
		saving: PropTypes.bool.isRequired,

		/**
		 * The status of the uploaded files while in progress.
		 */
		savingStatus: PropTypes.objectOf(PropTypes.shape({

			/**
			 * The unique ID (from Lokalise) for the file upload.
			 */
			process_id: PropTypes.string.isRequired,

			/**
			 * The status (from Lokalise) for the file upload.
			 */
			status: PropTypes.string.isRequired,

			/**
			 * The status message (from Lokalise), if any, for the file upload.
			 */
			message: PropTypes.string,

		})).isRequired,

		/**
		 * True, if the API call has succeeded.
		 */
		success: PropTypes.bool.isRequired,

		/**
		 * True, if the API call has failed.
		 */
		error: PropTypes.bool.isRequired,

	}).isRequired,

};

/**
 * Shared component to render the alerts for uploading content to Lokalise for translation.
 *
 * @param {Object} props The props.
 * @returns {Object} The rendered component.
 */
const TranslationsUploadToLokaliseAlerts = (props) => {

	const {
		apiStatusProject,
		apiStatusUploadFiles,
	} = props;

	const savingStatus = apiStatusUploadFiles?.savingStatus || {};

	const numFinished =
		Object.values(savingStatus).filter((fProcess) => fProcess.status === 'finished').length;

	const numTotal =
		Object.keys(savingStatus).length;

	const savingProgress = numTotal > 0 ? (numFinished / numTotal) * 100 : 0;

	return (
		<>
			{apiStatusProject.saving &&
				<InfoAlert
					className="d-flex align-items-center"
					prefix={false}>
					<Loading
						size="fa-lg"
						isLoading />
					<span className="ms-2">
						<strong>
							<FormattedMessage
								{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_1_LABEL} />
						</strong>
						{' '}
						<FormattedMessage
							{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_1_TEXT_SAVING} />
					</span>
				</InfoAlert>
			}
			{apiStatusProject.success &&
				<SuccessAlert prefix={false}>
					<strong className="ms-1">
						<FormattedMessage
							{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_1_LABEL} />
					</strong>
					{' '}
					<FormattedMessage
						{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_1_TEXT_SUCCESS} />
				</SuccessAlert>
			}
			{apiStatusProject.error &&
				<DangerAlert prefix={false}>
					<strong className="ms-1">
						<FormattedMessage
							{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_1_LABEL} />
					</strong>
					{' '}
					<FormattedMessage
						{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_1_TEXT_ERROR} />
				</DangerAlert>
			}
			{apiStatusUploadFiles.saving &&
				<InfoAlert
					className="d-flex align-items-center"
					prefix={false}>
					<Loading
						size="fa-lg"
						isLoading />
					<span className="ms-2">
						<strong>
							<FormattedMessage
								{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_2_LABEL} />
						</strong>
						{' '}
						<FormattedMessage
							{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_2_TEXT_SAVING}
							values={{
								progress: savingProgress.toFixed(0),
								bold: (chunks) => <strong>{chunks}</strong>,
							}} />
					</span>
				</InfoAlert>
			}
			{apiStatusUploadFiles.success &&
				<SuccessAlert prefix={false}>
					<strong className="ms-1">
						<FormattedMessage
							{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_2_LABEL} />
					</strong>
					{' '}
					<FormattedMessage
						{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_2_TEXT_SUCCESS}
						values={{
							link: (chunks) => (
								<a
									href={`https://app.lokalise.com/project/${apiStatusProject.project?.project_id}/?view=multi`}
									target="_blank"
									rel="noopener noreferrer nofollow">
									{chunks}
								</a>
							),
						}} />
				</SuccessAlert>
			}
			{apiStatusUploadFiles.error &&
				<DangerAlert prefix={false}>
					<strong className="ms-1">
						<FormattedMessage
							{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_2_LABEL} />
					</strong>
					{' '}
					<FormattedMessage
						{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_API_STATUS_STEP_2_TEXT_ERROR} />
				</DangerAlert>
			}
		</>
	);

};

TranslationsUploadToLokaliseAlerts.propTypes = propTypes;

export default TranslationsUploadToLokaliseAlerts;
